import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';

/**
 * Server-side speech-to-text via the Sarvam proxy (Saarika, Indian languages +
 * code-switching). Captures mic audio with MediaRecorder, encodes it to 16 kHz
 * mono WAV in the browser (no ffmpeg needed), and posts it to /scribe/transcribe.
 */
@Injectable({ providedIn: 'root' })
export class VoiceService {
  private mediaRecorder: any = null;
  private stream: any = null;
  private chunks: any[] = [];
  private readonly URL = `${environment.chatbotApiUrl}scribe/transcribe`;

  constructor(private http: HttpClient) {}

  get supported(): boolean {
    const md: any = navigator.mediaDevices;
    return !!(md && md.getUserMedia && (window as any).MediaRecorder);
  }

  isRecording(): boolean {
    return !!this.mediaRecorder && this.mediaRecorder.state !== 'inactive';
  }

  async start(): Promise<void> {
    const md: any = navigator.mediaDevices;
    this.stream = await md.getUserMedia({ audio: true });
    this.chunks = [];
    const mr = new (window as any).MediaRecorder(this.stream);
    mr.ondataavailable = (e: any) => { if (e.data && e.data.size > 0) this.chunks.push(e.data); };
    this.mediaRecorder = mr;
    mr.start();
  }

  /** Stop recording, transcribe, and resolve with the text (may be ''). */
  stop(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const mr = this.mediaRecorder;
      if (!mr) { resolve(''); return; }
      mr.onstop = async () => {
        const blob = new Blob(this.chunks, { type: mr.mimeType || 'audio/webm' });
        this.release();
        if (!blob.size) { resolve(''); return; }
        try {
          const wav = await this.blobToWav(blob);
          const form = new FormData();
          form.append('file', wav, 'audio.wav');
          form.append('language_code', 'unknown'); // auto-detect (code-switching)
          this.http.post<any>(this.URL, form).subscribe({
            next: (r) => resolve(((r && r.transcript) || '').trim()),
            error: (e) => reject(e),
          });
        } catch (e) {
          reject(e);
        }
      };
      try { mr.stop(); } catch (e) { reject(e); }
    });
  }

  /** Abort recording without transcribing (teardown). */
  cancel(): void {
    if (this.mediaRecorder) {
      try {
        this.mediaRecorder.onstop = null;
        if (this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop();
      } catch { /* ignore */ }
    }
    this.release();
  }

  private release(): void {
    if (this.stream) {
      try { this.stream.getTracks().forEach((t: any) => t.stop()); } catch { /* ignore */ }
      this.stream = null;
    }
    this.mediaRecorder = null;
    this.chunks = [];
  }

  private async blobToWav(blob: Blob): Promise<Blob> {
    const arrayBuf = await blob.arrayBuffer();
    const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
    let ctx: any;
    try { ctx = new AC({ sampleRate: 16000 }); } catch { ctx = new AC(); }
    const audioBuf: AudioBuffer = await ctx.decodeAudioData(arrayBuf);
    let samples: Float32Array = audioBuf.getChannelData(0);
    let sr = audioBuf.sampleRate;
    if (sr !== 16000) { samples = this.downsample(samples, sr, 16000); sr = 16000; }
    try { ctx.close(); } catch { /* ignore */ }
    return this.encodeWav(samples, sr);
  }

  private downsample(buf: Float32Array, from: number, to: number): Float32Array {
    if (to >= from) return buf;
    const ratio = from / to;
    const len = Math.round(buf.length / ratio);
    const out = new Float32Array(len);
    let oi = 0, bi = 0;
    while (oi < len) {
      const next = Math.round((oi + 1) * ratio);
      let sum = 0, c = 0;
      for (let i = bi; i < next && i < buf.length; i++) { sum += buf[i]; c++; }
      out[oi] = c ? sum / c : 0;
      oi++; bi = next;
    }
    return out;
  }

  private encodeWav(samples: Float32Array, sampleRate: number): Blob {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);
    const writeStr = (o: number, s: string) => {
      for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i));
    };
    writeStr(0, 'RIFF'); view.setUint32(4, 36 + samples.length * 2, true); writeStr(8, 'WAVE');
    writeStr(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true);
    view.setUint16(22, 1, true); view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
    writeStr(36, 'data'); view.setUint32(40, samples.length * 2, true);
    let o = 44;
    for (let i = 0; i < samples.length; i++) {
      const s = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(o, s < 0 ? s * 0x8000 : s * 0x7fff, true);
      o += 2;
    }
    return new Blob([view], { type: 'audio/wav' });
  }
}
